import React, {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Checkbox,
  Chip,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
  Link as BaseLink,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { DateTimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { Link } from 'react-router-dom';
import {
  SiteChoice,
  SiteOption,
  SmartImportDraftFind,
  SmartImportDraftRow,
} from '../types';
import { NONE_SITE_CHOICE, SiteSelect } from './SiteSelect';
import { defineMessages, useIntl } from 'react-intl';
import {
  useMaterialReactTableLocalization,
  useVocabulary,
} from '../../../i18n';
import { formatImportWarning } from '../importMessages';
import {
  SurfaceAutocomplete,
  SurfaceSelect,
  SurfaceTextField,
} from '../../../components/design-system';

type EditableFindField = keyof SmartImportDraftFind;
type TextFindField = 'name' | 'evId' | 'description' | 'note' | 'localization';

const paperStyles = { display: 'grid', flexGrow: 1, minHeight: 0 };
const containerStyles = { maxHeight: 'calc(100dvh - 310px)' };
const inputStyles = { minWidth: 140 };
const longInputStyles = { minWidth: 220 };
const siteInputStyles = { minWidth: 220 };
const warningIconStyles = { color: 'warning.main' };
const selectStyles = { minWidth: 110 };
const centeredCellProps = { align: 'center' as const };
const rightAlignedCellProps = { align: 'right' as const };
const decimalInputProps = { inputMode: 'decimal' as const };
const decimalInputPattern = /^[+-]?(?:\d*(?:\.\d*)?)$/;
const dateTimePickerSlotProps = {
  textField: { size: 'small' as const, sx: longInputStyles },
};
const findFieldMessages = defineMessages({
  name: { id: 'general.name', defaultMessage: 'Name' },
  evId: { id: 'find.evidenceId', defaultMessage: 'Evidence ID' },
  description: { id: 'general.description', defaultMessage: 'Description' },
  note: { id: 'general.note', defaultMessage: 'Note' },
  localization: { id: 'find.localization', defaultMessage: 'Localization' },
});
const coordinateMessages = defineMessages({
  latitude: { id: 'general.latitude', defaultMessage: 'Latitude' },
  longitude: { id: 'general.longitude', defaultMessage: 'Longitude' },
});

function formatFoundAt(value: string | null): string {
  if (!value) {
    return '—';
  }

  const date = DateTime.fromISO(value);

  return date.isValid ? date.toFormat('dd.MM.yyyy HH:mm') : value;
}

interface FindCellProps {
  disabled: boolean;
  field: TextFindField;
  rowRef: string;
  value: string | null;
  onChange: (
    rowRef: string,
    field: TextFindField,
    value: string | null
  ) => void;
  long?: boolean;
}

const FindCell = React.memo(
  ({
    disabled,
    field,
    rowRef,
    value,
    onChange,
    long = false,
  }: FindCellProps) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;

        onChange(rowRef, field, nextValue || null);
      },
      [field, onChange, rowRef]
    );

    if (disabled) {
      return <Typography variant="body2">{value ?? '—'}</Typography>;
    }

    return (
      <SurfaceTextField
        disabled={disabled}
        multiline={long}
        onChange={handleChange}
        surface="sunken"
        sx={long ? longInputStyles : inputStyles}
        type="text"
        value={value ?? ''}
      />
    );
  }
);

interface CoordinateCellProps {
  disabled: boolean;
  field: 'latitude' | 'longitude';
  rowRef: string;
  value: number | null;
  onChange: ImportReviewTableProps['onFindChange'];
}

const CoordinateCell = React.memo(
  ({ disabled, field, rowRef, value, onChange }: CoordinateCellProps) => {
    const [inputValue, setInputValue] = useState(
      value == null ? '' : String(value)
    );

    useEffect(() => {
      setInputValue(value == null ? '' : String(value));
    }, [value]);

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value.replace(',', '.');

      if (decimalInputPattern.test(nextValue)) {
        setInputValue(nextValue);
      }
    }, []);
    const handleBlur = useCallback(
      (_event: FocusEvent<HTMLInputElement>) => {
        if (inputValue === '') {
          onChange(rowRef, field, null);
          return;
        }

        const coordinate = Number(inputValue);

        if (Number.isFinite(coordinate)) {
          onChange(rowRef, field, coordinate);
        } else {
          setInputValue(value == null ? '' : String(value));
        }
      },
      [field, inputValue, onChange, rowRef, value]
    );

    if (disabled) {
      return <Typography variant="body2">{value ?? '—'}</Typography>;
    }

    return (
      <SurfaceTextField
        inputProps={decimalInputProps}
        onBlur={handleBlur}
        onChange={handleChange}
        surface="sunken"
        sx={inputStyles}
        type="text"
        value={inputValue}
      />
    );
  }
);

interface FoundAtCellProps {
  disabled: boolean;
  rowRef: string;
  value: string | null;
  onChange: ImportReviewTableProps['onFindChange'];
}

const FoundAtCell = React.memo(
  ({ disabled, rowRef, value, onChange }: FoundAtCellProps) => {
    const pickerValue = useMemo(() => {
      if (!value) {
        return null;
      }

      const date = DateTime.fromISO(value);

      return date.isValid ? date : null;
    }, [value]);
    const handleChange = useCallback(
      (nextValue: DateTime | null) => {
        const nextFoundAt =
          nextValue?.isValid === true ? nextValue.toISO() : null;

        onChange(rowRef, 'foundAt', nextFoundAt);
      },
      [onChange, rowRef]
    );

    if (disabled) {
      return <Typography variant="body2">{formatFoundAt(value)}</Typography>;
    }

    return (
      <DateTimePicker
        ampm={false}
        onChange={handleChange}
        slotProps={dateTimePickerSlotProps}
        value={pickerValue}
      />
    );
  }
);

interface EnumCellProps {
  disabled: boolean;
  field: 'era' | 'type' | 'material';
  options: string[];
  rowRef: string;
  value: string | null;
  onChange: ImportReviewTableProps['onFindChange'];
}

const EnumCell = React.memo(
  ({ disabled, field, options, rowRef, value, onChange }: EnumCellProps) => {
    const vocabulary = useVocabulary();
    const handleChange = useCallback(
      (_event: React.SyntheticEvent, nextValue: string | null) =>
        onChange(rowRef, field, nextValue),
      [field, onChange, rowRef]
    );
    const vocabularyKind =
      field === 'type' ? 'findType' : field === 'era' ? 'era' : 'material';
    const getOptionLabel = useCallback(
      (option: string) => vocabulary(vocabularyKind, option),
      [vocabulary, vocabularyKind]
    );
    const displayValue = vocabulary(vocabularyKind, value);
    const normalizedValue =
      field === 'era' ? value?.toLocaleLowerCase() ?? null : value;

    if (disabled) {
      return <Typography variant="body2">{displayValue || '—'}</Typography>;
    }

    return (
      <SurfaceAutocomplete
        disabled={disabled}
        getOptionLabel={getOptionLabel}
        onChange={handleChange}
        options={options}
        surface="sunken"
        sx={inputStyles}
        value={normalizedValue}
      />
    );
  }
);

interface ReviewSiteCellProps {
  disabled: boolean;
  rowRef: string;
  siteId: string | null;
  sites: SiteOption[];
  onChange: ImportReviewTableProps['onFindChange'];
}

const ReviewSiteCell = React.memo(
  ({ disabled, rowRef, siteId, sites, onChange }: ReviewSiteCellProps) => {
    const intl = useIntl();
    const value = useMemo<SiteChoice>(
      () =>
        siteId == null
          ? NONE_SITE_CHOICE
          : sites.find((site) => site.id === siteId) || {
              id: siteId,
              name: siteId,
            },
      [siteId, sites]
    );
    const handleChange = useCallback(
      (choice: SiteChoice | null) =>
        onChange(rowRef, 'siteId', choice?.id ?? null),
      [onChange, rowRef]
    );

    if (disabled) {
      return (
        <Typography variant="body2">
          {value.id === null
            ? intl.formatMessage({
                id: 'general.none',
                defaultMessage: 'None',
              })
            : value.name}
        </Typography>
      );
    }

    return (
      <Stack sx={siteInputStyles}>
        <SiteSelect
          disabled={disabled}
          label={intl.formatMessage({
            id: 'general.site',
            defaultMessage: 'Site',
          })}
          onChange={handleChange}
          sites={sites}
          value={value}
        />
      </Stack>
    );
  }
);

interface TagsCellProps {
  disabled: boolean;
  options: string[];
  rowRef: string;
  tags: string[];
  onChange: ImportReviewTableProps['onFindChange'];
}

const TagsCell = React.memo(
  ({ disabled, options, rowRef, tags, onChange }: TagsCellProps) => {
    const handleChange = useCallback(
      (_event: React.SyntheticEvent, values: string[]) =>
        onChange(rowRef, 'tags', values),
      [onChange, rowRef]
    );
    if (disabled) {
      return (
        <Stack direction="row" gap={0.5}>
          {tags.length
            ? tags.map((tag) => <Chip key={tag} label={tag} size="small" />)
            : '—'}
        </Stack>
      );
    }

    return (
      <SurfaceAutocomplete
        disabled={disabled}
        freeSolo
        multiple
        onChange={handleChange}
        options={options}
        surface="sunken"
        sx={longInputStyles}
        value={tags}
      />
    );
  }
);

interface VisibilityCellProps {
  disabled: boolean;
  rowRef: string;
  value: SmartImportDraftFind['visibility'];
  onChange: ImportReviewTableProps['onFindChange'];
}

const VisibilityCell = React.memo(
  ({ disabled, rowRef, value, onChange }: VisibilityCellProps) => {
    const intl = useIntl();
    const handleChange = useCallback(
      (event: SelectChangeEvent) =>
        onChange(rowRef, 'visibility', event.target.value),
      [onChange, rowRef]
    );

    if (disabled) {
      return (
        <Typography variant="body2">
          {value === 'private'
            ? intl.formatMessage({
                id: 'general.private',
                defaultMessage: 'Private',
              })
            : intl.formatMessage({
                id: 'general.public',
                defaultMessage: 'Public',
              })}
        </Typography>
      );
    }

    return (
      <SurfaceSelect
        disabled={disabled}
        onChange={handleChange}
        surface="sunken"
        sx={selectStyles}
        value={value}
      >
        <MenuItem value="private">
          {intl.formatMessage({
            id: 'general.private',
            defaultMessage: 'Private',
          })}
        </MenuItem>
        <MenuItem value="public">
          {intl.formatMessage({
            id: 'general.public',
            defaultMessage: 'Public',
          })}
        </MenuItem>
      </SurfaceSelect>
    );
  }
);

interface IncludeCellProps {
  disabled: boolean;
  include: boolean;
  rowRef: string;
  onChange: ImportReviewTableProps['onIncludeChange'];
}

const IncludeCell = React.memo(
  ({ disabled, include, rowRef, onChange }: IncludeCellProps) => {
    const handleChange = useCallback(
      (_event: ChangeEvent<HTMLInputElement>, checked: boolean) =>
        onChange(rowRef, checked),
      [onChange, rowRef]
    );

    return (
      <Checkbox checked={include} disabled={disabled} onChange={handleChange} />
    );
  }
);

interface ImportReviewTableProps {
  disabled: boolean;
  eraOptions: string[];
  findTypeOptions: string[];
  materialOptions: string[];
  tagOptions: string[];
  rows: SmartImportDraftRow[];
  sites: SiteOption[];
  committedFindIds?: string[];
  onFindChange: (
    rowRef: string,
    field: EditableFindField,
    value: SmartImportDraftFind[EditableFindField]
  ) => void;
  onIncludeChange: (rowRef: string, include: boolean) => void;
}

export const ImportReviewTable = React.memo(
  ({
    disabled,
    eraOptions,
    findTypeOptions,
    materialOptions,
    tagOptions,
    rows,
    sites,
    committedFindIds = [],
    onFindChange,
    onIncludeChange,
  }: ImportReviewTableProps) => {
    const intl = useIntl();
    const localization = useMaterialReactTableLocalization();
    const committedIdByRowRef = useMemo(() => {
      const ids = new Map<string, string>();
      let committedIndex = 0;

      for (const row of rows) {
        if (row.include && committedFindIds[committedIndex]) {
          ids.set(row.rowRef, committedFindIds[committedIndex]);
          committedIndex += 1;
        }
      }

      return ids;
    }, [committedFindIds, rows]);
    const columns = useMemo<MRT_ColumnDef<SmartImportDraftRow>[]>(
      () => [
        {
          id: 'include',
          header: intl.formatMessage({
            id: 'imports.review.include',
            defaultMessage: 'Import',
          }),
          size: 70,
          Cell: ({ row }) => (
            <IncludeCell
              disabled={disabled}
              include={row.original.include}
              onChange={onIncludeChange}
              rowRef={row.original.rowRef}
            />
          ),
        },
        ...(committedFindIds.length
          ? [
              {
                id: 'committedFindId',
                header: intl.formatMessage({
                  id: 'imports.review.findId',
                  defaultMessage: 'Find ID',
                }),
                size: 180,
                Cell: ({ row }) => {
                  const findId = committedIdByRowRef.get(row.original.rowRef);
                  const findPath = row.original.find.siteId
                    ? `/s/${row.original.find.siteId}/f/${findId}/edit`
                    : `/f/${findId}/edit`;

                  return findId ? (
                    <BaseLink component={Link} to={findPath}>
                      {findId}
                    </BaseLink>
                  ) : (
                    '—'
                  );
                },
              } as MRT_ColumnDef<SmartImportDraftRow>,
            ]
          : []),
        {
          accessorKey: 'sourceRowNumber',
          header: intl.formatMessage({
            id: 'imports.sourceRow',
            defaultMessage: 'Source row',
          }),
          muiTableBodyCellProps: rightAlignedCellProps,
          muiTableHeadCellProps: rightAlignedCellProps,
          size: 80,
        },
        {
          id: 'warnings',
          header: intl.formatMessage({
            id: 'imports.review.review',
            defaultMessage: 'Review',
          }),
          muiTableBodyCellProps: centeredCellProps,
          muiTableHeadCellProps: centeredCellProps,
          size: 75,
          Cell: ({ row }) =>
            row.original.needsReview || row.original.warnings.length ? (
              <Tooltip
                title={row.original.warnings
                  .map((warning) => formatImportWarning(warning, intl))
                  .join('\n')}
              >
                <WarningAmberOutlinedIcon sx={warningIconStyles} />
              </Tooltip>
            ) : (
              '—'
            ),
        },
        {
          id: 'siteId',
          header: intl.formatMessage({
            id: 'general.site',
            defaultMessage: 'Site',
          }),
          size: 230,
          Cell: ({ row }) => (
            <ReviewSiteCell
              disabled={disabled}
              onChange={onFindChange}
              rowRef={row.original.rowRef}
              siteId={row.original.find.siteId}
              sites={sites}
            />
          ),
        },
        ...(['name', 'evId'] as const).map((field) => ({
          id: field,
          header: intl.formatMessage(findFieldMessages[field]),
          size: 170,
          Cell: ({ row }) => (
            <FindCell
              disabled={disabled}
              field={field}
              onChange={onFindChange}
              rowRef={row.original.rowRef}
              value={row.original.find[field]}
            />
          ),
        })),
        {
          id: 'foundAt',
          header: intl.formatMessage({
            id: 'find.foundAt',
            defaultMessage: 'Found at',
          }),
          size: 220,
          Cell: ({ row }) => (
            <FoundAtCell
              disabled={disabled}
              onChange={onFindChange}
              rowRef={row.original.rowRef}
              value={row.original.find.foundAt}
            />
          ),
        },
        {
          id: 'type',
          header: intl.formatMessage({
            id: 'find.type',
            defaultMessage: 'Type',
          }),
          size: 180,
          Cell: ({ row }) => (
            <EnumCell
              disabled={disabled}
              field="type"
              onChange={onFindChange}
              options={findTypeOptions}
              rowRef={row.original.rowRef}
              value={row.original.find.type}
            />
          ),
        },
        {
          id: 'material',
          header: intl.formatMessage({
            id: 'find.material',
            defaultMessage: 'Material',
          }),
          size: 180,
          Cell: ({ row }) => (
            <EnumCell
              disabled={disabled}
              field="material"
              onChange={onFindChange}
              options={materialOptions}
              rowRef={row.original.rowRef}
              value={row.original.find.material}
            />
          ),
        },
        {
          id: 'era',
          header: intl.formatMessage({
            id: 'find.era',
            defaultMessage: 'Era',
          }),
          size: 180,
          Cell: ({ row }) => (
            <EnumCell
              disabled={disabled}
              field="era"
              onChange={onFindChange}
              options={eraOptions}
              rowRef={row.original.rowRef}
              value={row.original.find.era}
            />
          ),
        },
        ...(['description', 'note', 'localization'] as const).map((field) => ({
          id: field,
          header: intl.formatMessage(findFieldMessages[field]),
          size: 240,
          Cell: ({ row }) => (
            <FindCell
              disabled={disabled}
              field={field}
              long={field !== 'localization'}
              onChange={onFindChange}
              rowRef={row.original.rowRef}
              value={row.original.find[field]}
            />
          ),
        })),
        ...(['latitude', 'longitude'] as const).map((field) => ({
          id: field,
          header: intl.formatMessage(coordinateMessages[field]),
          size: 140,
          Cell: ({ row }) => (
            <CoordinateCell
              disabled={disabled}
              field={field}
              onChange={onFindChange}
              rowRef={row.original.rowRef}
              value={row.original.find[field]}
            />
          ),
        })),
        {
          id: 'visibility',
          header: intl.formatMessage({
            id: 'find.visibility',
            defaultMessage: 'Visibility',
          }),
          size: 120,
          Cell: ({ row }) => (
            <VisibilityCell
              disabled={disabled}
              onChange={onFindChange}
              rowRef={row.original.rowRef}
              value={row.original.find.visibility}
            />
          ),
        },
        {
          id: 'tags',
          header: intl.formatMessage({
            id: 'general.tags',
            defaultMessage: 'Tags',
          }),
          size: 240,
          Cell: ({ row }) => (
            <TagsCell
              disabled={disabled}
              onChange={onFindChange}
              options={tagOptions}
              rowRef={row.original.rowRef}
              tags={row.original.find.tags}
            />
          ),
        },
      ],
      [
        committedFindIds.length,
        committedIdByRowRef,
        disabled,
        eraOptions,
        findTypeOptions,
        intl,
        materialOptions,
        onFindChange,
        onIncludeChange,
        sites,
        tagOptions,
      ]
    );
    const table = useMaterialReactTable({
      columns,
      data: rows,
      localization,
      enableColumnActions: false,
      enableColumnFilters: false,
      enableDensityToggle: false,
      enableFullScreenToggle: true,
      getRowId: (row) => row.rowRef,
      initialState: {
        density: 'compact',
        pagination: { pageIndex: 0, pageSize: 50 },
      },
      muiPaginationProps: { rowsPerPageOptions: [25, 50, 100] },
      muiTableContainerProps: { sx: containerStyles },
      muiTablePaperProps: { sx: paperStyles },
    });

    return <MaterialReactTable table={table} />;
  }
);
